import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { extractError, useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function InvoiceCreate() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const bookingId = search.get("booking_id");
  const clientId = search.get("client_id");
  const [booking, setBooking] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    booking_id: bookingId || null,
    client_id: clientId || null,
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    line_items: [{ description: "", quantity: 1, rate: 0 }],
    discount_amount: 0,
    discount_percent: 0,
    tax_rate: company?.tax_rate ?? 18,
    payment_terms: "",
    notes: "",
    show_tnc: true,
    terms_text: company?.terms_and_conditions || "",
  });

useEffect(() => {
  const params = bookingId ? { booking_id: bookingId } : {};
  api.get("/clients/dropdown", { params }).then(({ data }) => setClients(data));
}, [bookingId]);

  useEffect(() => {
    if (company) {
      setForm((f) => ({ ...f, tax_rate: company.tax_rate ?? f.tax_rate, terms_text: f.terms_text || company.terms_and_conditions || "" }));
    }
  }, [company]);

  const symbol = company?.currency_symbol || "₹";

  const subtotal = form.line_items.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.rate) || 0), 0);
  let discount = Number(form.discount_amount) || 0;
  if (form.discount_percent) discount = subtotal * (Number(form.discount_percent) / 100);
  const afterDisc = subtotal - discount;
  const tax = afterDisc * ((Number(form.tax_rate) || 0) / 100);
  const total = afterDisc + tax;

  const setLI = (idx, field, val) => {
    const arr = [...form.line_items];
    arr[idx] = { ...arr[idx], [field]: val };
    setForm({ ...form, line_items: arr });
  };
  const addLI = () => setForm({ ...form, line_items: [...form.line_items, { description: "", quantity: 1, rate: 0 }] });
  const removeLI = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const submit = async (e, action) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      if (!form.client_id) {
        toast.error("Select a client");
        setSaving(false);
        return;
      }
      const payload = {
        ...form,
        line_items: form.line_items.map((li) => ({ description: li.description, quantity: Number(li.quantity) || 0, rate: Number(li.rate) || 0 })),
        discount_amount: Number(form.discount_amount) || 0,
        discount_percent: Number(form.discount_percent) || 0,
        tax_rate: Number(form.tax_rate) || 0,
      };
      const { data } = await api.post("/invoices", payload);
      if (action === "submit") {
        await api.post(`/invoices/${data.id}/submit`);
      }
      toast.success(action === "submit" ? "Submitted for approval" : "Draft saved");
      navigate(`/app/invoices/${data.id}`);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="invoice-create-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Billing</div>
        <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Create invoice</h1>
        {booking && <div className="text-sm text-zinc-600 mt-1">For booking: {booking.client_name} · {booking.event_date}</div>}
      </div>

      <form className="bg-white border border-black/10 rounded-md p-6 md:p-8 space-y-6" onSubmit={(e) => submit(e, "save")}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Client *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="invoice-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Invoice date</Label>
            <Input type="date" className="mt-1.5 h-11" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="invoice-date-input" />
          </div>
          <div>
            <Label>Due date</Label>
            <Input type="date" className="mt-1.5 h-11" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Line items</Label>
            <Button type="button" variant="outline" size="sm" onClick={addLI} data-testid="invoice-add-line-btn"><Plus size={14} className="mr-1" /> Add line</Button>
          </div>
          <div className="space-y-3">
            {form.line_items.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-line-${idx}`}>
                <Input className="col-span-6 h-11" placeholder="Description" value={li.description} onChange={(e) => setLI(idx, "description", e.target.value)} data-testid={`invoice-line-desc-${idx}`} />
                <Input type="number" className="col-span-2 h-11" placeholder="Qty" value={li.quantity} onChange={(e) => setLI(idx, "quantity", e.target.value)} data-testid={`invoice-line-qty-${idx}`} />
                <Input type="number" className="col-span-3 h-11" placeholder="Rate" value={li.rate} onChange={(e) => setLI(idx, "rate", e.target.value)} data-testid={`invoice-line-rate-${idx}`} />
                <Button type="button" variant="ghost" size="icon" className="col-span-1 h-11 text-red-600" onClick={() => removeLI(idx)} disabled={form.line_items.length === 1} data-testid={`invoice-line-remove-${idx}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Discount amount</Label>
            <Input type="number" className="mt-1.5 h-11" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value, discount_percent: 0 })} />
          </div>
          <div>
            <Label>Discount %</Label>
            <Input type="number" className="mt-1.5 h-11" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value, discount_amount: 0 })} />
          </div>
          <div>
            <Label>Tax rate %</Label>
            <Input type="number" className="mt-1.5 h-11" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="invoice-tax-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Payment terms</Label>
            <Input className="mt-1.5 h-11" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. 50% advance, balance on event day" />
          </div>
          <div>
            <Label>Notes</Label>
            <Input className="mt-1.5 h-11" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Thank you for your business" />
          </div>
        </div>

        <div>
          <Label>Terms & conditions (shown on invoice)</Label>
          <Textarea className="mt-1.5" rows={3} value={form.terms_text} onChange={(e) => setForm({ ...form, terms_text: e.target.value })} />
        </div>

        <div className="bg-[#F5F4F0] rounded-md p-5 grid grid-cols-2 gap-2 text-sm">
          <div>Subtotal</div><div className="text-right">{symbol}{subtotal.toFixed(2)}</div>
          <div>Discount</div><div className="text-right">-{symbol}{discount.toFixed(2)}</div>
          <div>Tax ({form.tax_rate}%)</div><div className="text-right">{symbol}{tax.toFixed(2)}</div>
          <div className="font-serif text-2xl">Total</div><div className="font-serif text-2xl text-right" data-testid="invoice-total">{symbol}{total.toFixed(2)}</div>
        </div>

        <div className="flex flex-wrap justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)} data-testid="invoice-cancel-btn">Cancel</Button>
          <Button type="button" disabled={saving} onClick={(e) => submit(e, "submit")} className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full px-6" data-testid="invoice-submit-approval-btn">
            Save
          </Button>
        </div>
      </form>
    </div>
  );
}
